/**
 * Addition-series detection over pivot distances counted back from bar 0.
 *
 * An addition series is Fibonacci-shaped but free to start anywhere: after the anchor at 0, two
 * seed distances a and b, then every following term is the sum of the previous two.
 *
 *   0, 3, 8, 11, 19, 30, ...      seeds 3 and 8, then 3+8=11, 8+11=19, 11+19=30
 *   0, 6, 11, 17, 28, 45, ...     seeds 6 and 11, then 6+11=17, 11+17=28, 17+28=45
 *
 * Note the 0 is an anchor, not a term: 0+3 does not give 8. Treating it as a term would force
 * every series to start with a doubling and would find almost none of them.
 *
 * A term counts as present when some pivot sits within `tolerance` bars of it (±1 by default).
 */

/** Terms a series must match, beyond the anchor, to be reported. */
export const DEFAULT_MIN_TERMS = 5;

/** Bars of slack when matching a computed term against a real pivot. */
export const DEFAULT_TOLERANCE = 1;

/**
 * The pivot nearest a target distance, or null when none is within tolerance.
 *
 * Nearest rather than first: with ±1 slack two pivots can both qualify, and taking whichever the
 * scan reached first would make the result depend on input order.
 *
 * Kept as the readable reference implementation and used directly by callers with a handful of
 * items; the series search goes through the indexed version below, because scanning every pivot
 * per lookup is what made a full-history chart take minutes.
 */
export function nearestWithin(items, target, tolerance) {
  let best = null;
  let bestDelta = Infinity;
  for (const item of items) {
    const delta = Math.abs(item.distance - target);
    if (delta <= tolerance && delta < bestDelta) {
      best = item;
      bestDelta = delta;
    }
  }
  return best;
}

/**
 * Distance -> pivot lookup, so a term can be tested in constant time.
 *
 * Distances are whole bar counts, so an exact integer key is enough; the tolerance is applied by
 * probing the few neighbouring keys rather than by scanning. Where several pivots share a
 * distance the first wins, which is stable because the input is sorted.
 */
export function indexByDistance(items) {
  const map = new Map();
  for (const item of items) {
    if (!map.has(item.distance)) map.set(item.distance, item);
  }
  return map;
}

/** Indexed equivalent of nearestWithin: same answer, without touching every pivot. */
export function nearestIndexed(map, target, tolerance) {
  let best = null;
  let bestDelta = Infinity;
  const lo = Math.ceil(target - tolerance);
  const hi = Math.floor(target + tolerance);
  for (let d = lo; d <= hi; d++) {
    const item = map.get(d);
    if (!item) continue;
    const delta = Math.abs(d - target);
    if (delta < bestDelta) {
      best = item;
      bestDelta = delta;
    }
  }
  return best;
}

/**
 * Grows one addition series from a seed pair and reports the pivots it matched.
 *
 * Growth uses the IDEAL terms (exact sums), not the matched pivots' actual distances. Feeding the
 * matched values back in would let a ±1 slip compound: each term would inherit the previous
 * errors, and after a few terms the series would drift far enough to match essentially anything.
 */
export function growSeries(items, a, b, tolerance = DEFAULT_TOLERANCE, prebuilt = null) {
  const map = prebuilt || indexByDistance(items);
  const maxDistance = items.reduce((m, i) => Math.max(m, i.distance), 0);
  const matched = [];

  const first = nearestIndexed(map, a, tolerance);
  const second = nearestIndexed(map, b, tolerance);
  if (!first || !second) return { terms: [], matched: [] };

  const terms = [a, b];
  matched.push({ term: a, pivot: first }, { term: b, pivot: second });

  let prev = a;
  let cur = b;
  // Terms grow at least as fast as Fibonacci, so this is bounded tightly by maxDistance; the
  // iteration cap is a guard against a degenerate seed pair, not a real limit.
  for (let guard = 0; guard < 64; guard++) {
    const next = prev + cur;
    if (next > maxDistance + tolerance) break;
    const hit = nearestIndexed(map, next, tolerance);
    if (!hit) break;
    terms.push(next);
    matched.push({ term: next, pivot: hit });
    prev = cur;
    cur = next;
  }

  return { terms, matched };
}

/**
 * Finds addition series among pivot distances.
 *
 * @param {Array<{distance:number}>} items pivot distances from the anchor (0 = the anchor bar)
 * @param {object} [opts]
 * @param {number} [opts.tolerance=1]
 * @param {number} [opts.minTerms=5] terms required beyond the anchor
 * @returns {Array<{seeds:number[], terms:number[], matched:Array, length:number}>} longest first
 */
export function findAdditionSeries(items, opts = {}) {
  const tolerance = opts.tolerance ?? DEFAULT_TOLERANCE;
  const minTerms = opts.minTerms ?? DEFAULT_MIN_TERMS;
  if (!Array.isArray(items) || items.length === 0) return [];

  // Distinct distances greater than zero. The anchor itself is not a seed, and duplicates would
  // re-derive the same series once per repeat.
  const distances = [...new Set(items.map(i => i.distance).filter(d => d > 0))].sort((x, y) => x - y);

  const found = [];
  const seen = new Set();
  const map = indexByDistance(items);
  const maxDistance = distances.length ? distances[distances.length - 1] : 0;

  // Largest term reachable with minTerms terms, as Fibonacci coefficients of the seeds:
  // a, b, a+b, a+2b, 2a+3b, 3a+5b ... Requiring that term to fit inside the chart lets most seed
  // pairs be rejected by arithmetic instead of by growing a series and discovering it runs off the
  // end. With thousands of pivots the pair loop is the whole cost, so this is what makes it
  // tractable rather than the constant-time lookups alone.
  // Terms are (1,0), (0,1), (1,1), (1,2), (2,3), (3,5)... in (a, b) coefficients — Fibonacci, as
  // the definition implies. These are the coefficients of the minTerms-th term.
  let prevA = 1, prevB = 0;
  let coefA = 0, coefB = 1;
  for (let t = 3; t <= minTerms; t++) {
    const nextA = prevA + coefA;
    const nextB = prevB + coefB;
    prevA = coefA; prevB = coefB;
    coefA = nextA; coefB = nextB;
  }
  const limit = maxDistance + tolerance;

  for (let i = 0; i < distances.length; i++) {
    const a = distances[i];
    // b is always greater than a, so the minTerms-th term exceeds (coefA + coefB) * a. Once that
    // passes the end of the chart no later seed can qualify either, since a only grows.
    if ((coefA + coefB) * a > limit) break;
    for (let j = i + 1; j < distances.length; j++) {
      const b = distances[j];
      // b only grows from here, so the rest of this row is out of reach too.
      if (coefA * a + coefB * b > limit) break;

      // Reject on the third term before doing anything else. The overwhelming majority of seed
      // pairs die here, and growSeries would first re-look-up a and b — which are drawn from this
      // very list and so always match — before reaching the same conclusion more expensively.
      if (!nearestIndexed(map, a + b, tolerance)) continue;

      const { terms, matched } = growSeries(items, a, b, tolerance, map);
      if (terms.length < minTerms) continue;

      // Two seed pairs one bar apart generate near-identical series. Keying on the pivots actually
      // matched collapses those into one result instead of reporting the same run repeatedly.
      const key = matched.map(m => m.pivot.index).join(',');
      if (seen.has(key)) continue;
      seen.add(key);

      found.push({ seeds: [a, b], terms, matched, length: terms.length });
    }
  }

  // Longest first: a longer run is the stronger claim, and callers that draw only the best one
  // should get that rather than whichever pair the scan happened to reach first.
  return found.sort((x, y) => y.length - x.length || x.seeds[0] - y.seeds[0]);
}

export default findAdditionSeries;
